import React, { useState } from "react";
import { motion } from "framer-motion";
import { Mail, Phone, MapPin, ArrowUpRight, Sparkles } from "lucide-react";
import { toast } from "sonner";
import { profile, valueProps } from "../../mock/mock";

// ─────────────────────────────────────────────────────────────
// HOW TO ACTIVATE EMAIL SENDING (one-time setup, free):
//  1. Go to https://web3forms.com
//  2. Enter your email: mr.anandbaral@gmail.com → click "Create Access Key"
//  3. Check your Gmail inbox → copy the Access Key they send you
//  4. Replace "YOUR_ACCESS_KEY_HERE" below with that key
//  5. Done — form submissions will arrive in your Gmail
// ─────────────────────────────────────────────────────────────
const WEB3FORMS_ACCESS_KEY = "ee8a5a91-e70c-4025-930a-ed4fdc966e6f";

export default function Contact() {
  const [form, setForm] = useState({ firstName: "", lastName: "", email: "", phone: "", message: "" });
  const [loading, setLoading] = useState(false);
  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.firstName || !form.email || !form.phone) {
      toast.error("Please fill your name, email and phone.");
      return;
    }
    setLoading(true);
    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        headers: { "Content-Type": "application/json", Accept: "application/json" },
        body: JSON.stringify({
          access_key: WEB3FORMS_ACCESS_KEY,
          subject: "New Message from anandbaral.in",
          from_name: `${form.firstName} ${form.lastName}`.trim(),
          email: form.email,
          phone: form.phone,
          message: form.message || "No message provided.",
          redirect: false,
        }),
      });
      const result = await response.json();
      if (result.success) {
        toast.success("Thanks — your message landed. I'll be in touch shortly.");
        setForm({ firstName: "", lastName: "", email: "", phone: "", message: "" });
      } else {
        toast.error("Something went wrong. Please try again or email me directly.");
      }
    } catch (err) {
      toast.error("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="contact" className="relative py-24 md:py-32 border-t border-white/5">
      <div className="mx-auto max-w-[1400px] px-6 md:px-10 grid grid-cols-1 lg:grid-cols-12 gap-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6 }}
          className="lg:col-span-5">
          <div className="section-label mb-6">Get In Touch</div>
          <h2 className="font-display text-[46px] md:text-[68px] leading-[0.98] tracking-[-0.02em]">
            Ready to grow your <span className="italic sage-text">brand</span>?
          </h2>
          <p className="mt-6 text-[17px] leading-[1.75] text-[var(--text-dim)] max-w-md">
            Tell me about your project — websites, Meta Ads, branding or the full creative stack. I typically respond within a business day.
          </p>

          <div className="mt-10 space-y-5">
            <a href={`mailto:${profile.email}`} className="group flex items-center justify-between p-5 rounded-2xl glass card-hover">
              <div className="flex items-center gap-4">
                <span className="h-11 w-11 rounded-full bg-[var(--sage)]/15 border border-[var(--sage)]/30 inline-flex items-center justify-center text-[var(--sage)]"><Mail size={16} /></span>
                <div>
                  <div className="mono text-[10px] tracking-[0.2em] uppercase text-[var(--muted)]">Email</div>
                  <div className="mt-1">{profile.email}</div>
                </div>
              </div>
              <ArrowUpRight size={18} className="text-[var(--text-dim)] group-hover:text-[var(--sage)] transition-colors" />
            </a>

            <a href={`tel:${profile.phoneLink}`} className="group flex items-center justify-between p-5 rounded-2xl glass card-hover">
              <div className="flex items-center gap-4">
                <span className="h-11 w-11 rounded-full bg-[var(--sage)]/15 border border-[var(--sage)]/30 inline-flex items-center justify-center text-[var(--sage)]"><Phone size={16} /></span>
                <div>
                  <div className="mono text-[10px] tracking-[0.2em] uppercase text-[var(--muted)]">Phone</div>
                  <div className="mt-1">{profile.phone}</div>
                </div>
              </div>
              <ArrowUpRight size={18} className="text-[var(--text-dim)] group-hover:text-[var(--sage)] transition-colors" />
            </a>

            <div className="flex items-center gap-4 p-5 rounded-2xl glass">
              <span className="h-11 w-11 rounded-full bg-[var(--sage)]/15 border border-[var(--sage)]/30 inline-flex items-center justify-center text-[var(--sage)]"><MapPin size={16} /></span>
              <div>
                <div className="mono text-[10px] tracking-[0.2em] uppercase text-[var(--muted)]">Based In</div>
                <div className="mt-1">{profile.location}</div>
              </div>
            </div>
          </div>

          <div className="mt-10 p-5 rounded-2xl glass-strong">
            <div className="flex items-center gap-2 mono text-[11px] tracking-[0.2em] uppercase text-[var(--sage)]">
              <Sparkles size={14} /> The Value of Working With Me
            </div>
            <ul className="mt-4 space-y-2 text-[14.5px] text-[var(--text-dim)]">
              {valueProps.map((v) => (
                <li key={v} className="flex items-start gap-2">
                  <span className="mt-2 h-1 w-1 rounded-full bg-[var(--sage)]" />{v}
                </li>
              ))}
            </ul>
          </div>
        </motion.div>

        <motion.form
          initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.6, delay: 0.1 }}
          onSubmit={submit}
          className="lg:col-span-7 rounded-3xl glass-strong p-8 md:p-12">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-2">
            <div>
              <label className="mono text-[10px] tracking-[0.2em] uppercase text-[var(--muted)]">First Name *</label>
              <input className="premium-input" placeholder="Enter your first name" value={form.firstName} onChange={update("firstName")} />
            </div>
            <div>
              <label className="mono text-[10px] tracking-[0.2em] uppercase text-[var(--muted)]">Last Name</label>
              <input className="premium-input" placeholder="Enter your last name" value={form.lastName} onChange={update("lastName")} />
            </div>
            <div>
              <label className="mono text-[10px] tracking-[0.2em] uppercase text-[var(--muted)]">Email Address *</label>
              <input type="email" className="premium-input" placeholder="you@brand.com" value={form.email} onChange={update("email")} />
            </div>
            <div>
              <label className="mono text-[10px] tracking-[0.2em] uppercase text-[var(--muted)]">Phone *</label>
              <input className="premium-input" placeholder="+91 ··········" value={form.phone} onChange={update("phone")} />
            </div>
            <div className="md:col-span-2">
              <label className="mono text-[10px] tracking-[0.2em] uppercase text-[var(--muted)]">Tell me about your project</label>
              <textarea rows={4} className="premium-input resize-none" placeholder="What are you looking to build, launch or grow?" value={form.message} onChange={update("message")} />
            </div>
          </div>

          <div className="mt-10 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <p className="mono text-[11px] tracking-[0.14em] uppercase text-[var(--muted)]">
              Replies within 24 hours · Based in {profile.location.split(",")[0]}
            </p>
            <button type="submit" disabled={loading} className="btn-primary disabled:opacity-60">
              {loading ? "Sending…" : "Submit Inquiry"} <ArrowUpRight size={18} />
            </button>
          </div>
        </motion.form>
      </div>
    </section>
  );
}
